import { aashto, findZr } from '@/modules/design/lib/calculations/calculationUtils';
import { IAsphaltDesignImperial } from '@/modules/design/interfaces/newAsphalt';
import { IAsphaltOutputObj, SnOutput } from '@/modules/design/interfaces/shared';

// Project Type: New-Asphalt
export const calculateFlexibleDesign = function (data: IAsphaltDesignImperial): IAsphaltOutputObj | null {
  const CeilHalf = function (amount: number) {
    const temp_amount = Math.ceil(amount);
    const compare = temp_amount - amount;
    let final_amount = temp_amount;
    if (compare > 0.5) {
      final_amount = temp_amount - 0.5;
    }
    return final_amount;
  };

  const findDepth = function (esal: number,
                              zr: number,
                              std_error: number,
                              delta_psi: number,
                              mr: number,
                              a: number,
                              min_d: number,
                              m: number,
                              prior_sn: number): SnOutput {
    const calculated_sn = aashto(esal, zr, std_error, delta_psi, mr);
    const sn_star = calculated_sn - prior_sn;

    let depth = CeilHalf(sn_star / a);
    const temp_d_surf = depth;

    if (depth < min_d) {
      depth = min_d;
    }
    if (depth < 0) {
      depth = 0.0;
    }
    const obj: SnOutput = {
      calculated_sn,
      sn_star,
      prior_sn,
      sn: depth * a * m,
      depth_star: temp_d_surf,
      depth,
      alternate_depth: 0,
    };

    return obj;
  };

  if (data.roadwayClassification == 'Parking Lot – Light Duty') {
    return null;
  }
  const num_base = data.substructureLayers.length;
  const reliability = data.reliabilityLevel!;
  const zr = findZr(reliability);
  const delta_psi = Math.abs(data.initialServiceability! - data.terminalServiceability!);
  const esal = data.totalDesignEsals!;
  const std_error = data.standardError!;
  const subgrade = data.subgrade!;
  const a_surf = data.layerCoefficient!;
  const allLayers = [];

  // We better be sure that these do in fact exist!!
  const multiple_lifts = data.multipleLifts;
  const surface_layers = data.surfaceLayers;
  const layers = data.substructureLayers;

  let layer: any;

  // if surface has lifts, use them for surface layers
  // otherwise use a single layer based on the min thickness and coef
  if (multiple_lifts) {
    // TODO: Can we be sure that surface layers exist here?
    surface_layers.forEach((l) => {
      allLayers.push({
        coefficient: (l.coefficient),
        drainage: (l.drainage),
        type: `${l.liftTitle} (AC)`,
        minThickness: l.thickness,
        thickness: l.thickness,
        style: 'asphalt',
      });
    });
  } else {
    allLayers.push({
      coefficient: data.layerCoefficient,
      drainage: data.drainageCoefficient,
      minThickness: data.minimumThickness,
      thickness: 0,
      type: 'Surface',
      style: 'asphalt',
    });
  }
  layers.forEach((l: any) => {
    allLayers.push({
      coefficient: l.coefficient,
      drainage: l.drainage,
      modulus: l.modulus,
      type: l.type,
      minThickness: (l.thickness),
      thickness: 0,
      fixedThickness: l.fixedThickness,
      style: 'base',
    });
  });
  allLayers.push({
    coefficient: 0.0,
    drainage: 0.0,
    modulus: data.subgrade,
    type: 'Subgrade',
    minThickness: 0.0,
    thickness: 0,
  });

  // Get design SN from aashto calculation
  const obj: IAsphaltOutputObj = {
    design_sn: aashto(esal, zr, std_error, delta_psi, subgrade),
    total_sn: 0,
    target_layer_depth: 0,
    layers: [],
    layerCalculations: [],
    warning: null,
    known_sn: 0,
    remaining_sn: 0,
    total_surface_depth: 0,
    calculated_sn: null,

    // Added to appease the typescript gods
    errors: '',
  };



  let current_sn = 0;
  const stepIndex = 1;
  const currentSurfaceDepth = 0;

  // loop through base layers
  for (let i = 0; i < allLayers.length - 1; i++) {
    let output: SnOutput = {
      alternate_depth: 0,
      depth: 0,
      depth_star: 0,
      sn: 0,
      sn_star: 0,
      prior_sn: 0,
      calculated_sn: 0,
    };

    layer = allLayers[i];
    const mr = <number>allLayers[i + 1].modulus; // Possibly undefined?
    const a = layer.coefficient;
    const m = layer.drainage;
    const d = layer.minThickness;
    let { thickness } = layer;
    if (layer.fixedThickness) {
      thickness = d;
      const tmp = findDepth(esal, zr, std_error, delta_psi, mr, a, d, m, current_sn);
      output.alternate_depth = tmp.depth_star;
    }
    if (thickness > 0 || (data.multipleLifts && i < 2)) {
      output.depth = thickness;
      output.depth_star = thickness;
      output.sn = thickness * a * m;
      output.sn_star = thickness * a * m;
      output.prior_sn = 0;
      output.calculated_sn = thickness * a * m;
    } else {
      if (isNaN(mr) || isNaN(zr) || isNaN(std_error!) || isNaN(delta_psi) || isNaN(a) || isNaN(d) || isNaN(m)) {
        obj.errors = '<p>New features have been incorporated into PaveXpress to enhance your current design.</p>  <p>In order to view the updated calculated design you must revisit the design steps and enter in the additional required information as noted. This will help produce a more accurate design. <a href="#information">Click here to update this design.</a></p>';
      }
      output = findDepth(esal, zr, std_error, delta_psi, mr, a, d, m, current_sn);
    }
    obj.layers.push({
      label: layer.type,
      depth: output.depth,
      type: layer.style,
      showLayer: output.depth > 0,
    });


    obj.layerCalculations.push({
      label: `Step ${i + 1}: ${layer.type}`,
      subgrade_mr: mr,
      sn: output.sn,
      sn_star: output.sn_star,
      depth: output.depth,
      depth_star: output.depth_star,
      minDepth: d,
      a,
      prior_sn: output.prior_sn,
      calculated_sn: output.calculated_sn,
      thickness_provided: (thickness > 0),
      layer_coefficient: a,
      drainage_coefficient: m,
      thickness: d,
      alternate_depth: output.alternate_depth,
      layer_type: layer.type,
      next_layer_type: allLayers[i + 1].type,
      extra_depth: 0,
    });
    current_sn += output.sn;
    obj.total_sn += output.sn;
    obj.total_surface_depth += output.depth;
  }

  if ((obj.total_sn - obj.design_sn) > 0.27) {
    obj.warning = 'The Design SN exceeds the Required SN due to the layer protection check. A base layer thickness can be reduced; however, the reduction may create issues with construction. Therefore, care must be taken before adjusting the fixed or minimum thickness.';
  }

  let coefficient;
  let drainage;
  let additional_depth;
  let new_depth;

  if (obj.total_sn < obj.design_sn) {
    if (multiple_lifts) {
      layer = surface_layers[2];
      coefficient = layer.coefficient;
      drainage = (layer.drainage);
      additional_depth = CeilHalf((obj.design_sn - obj.total_sn) / (coefficient * drainage));
      new_depth = (obj.layers[2].depth) + additional_depth;
      obj.layers[2].depth = new_depth;
      obj.total_sn += additional_depth * coefficient * drainage;
      obj.layerCalculations[2].extra_depth = additional_depth;
    } else {
      coefficient = data.layerCoefficient!;
      drainage = data.drainageCoefficient!;
      additional_depth = CeilHalf((obj.design_sn - obj.total_sn) / (coefficient * drainage));
      new_depth = (obj.layers[0].depth) + additional_depth;
      obj.layers[0].depth = new_depth;
      obj.total_sn += additional_depth * coefficient * drainage;
      obj.layerCalculations[0].extra_depth = additional_depth;
    }
  }
  obj.remaining_sn = (obj.design_sn - obj.total_sn);

  return obj;
};
